import React from 'react';
import { connect } from 'react-redux';
import { KeyLicenseTypes } from '../common/keyLicenseTypes';
import TooltipElement from '../components/tooltipElement';
import TooltipButtonGroup from '../components/tooltipButtonGroup';
import PermanentLicenseTable from '../components/permanentLicenseTable';
import OverlayLoader from '../components/overlayLoader';
import TableSelectionManager from '../components/tableSelectionManager';
import * as permanentLicensesActions from '../store/permanentLicensesSlice';
import { permanentLicensesSelectors } from '../store/permanentLicensesSlice';
import * as objectDataActions from '../store/objectDataSlice';
import { objectDataNames, objectDataSelectors } from '../store/objectDataSlice';
import KeyLicenseEditDialog from './keyLicenseEditDialog';
import ModalMessage from './modalMessage';
import RemoveRestoreLicsConfirmDialog from './removeRestoreLicsConfirmDialog';
import DeactivateLicConfirmDialog from './deactivateLicConfirmDialog';
import PermanentLicenseHistoryDialog from './permanentLicenseHistoryDialog';
import ReactivatePermanentLicenseDialog from './reactivatePermanentLicenseDialog';
import OnlineLicenseActivatingDialog from './onlineLicenseActivationDialog';
import './common.css';
import setTitleToFilters from '../common/filtersTitles';
import { isClipboardAvailable } from '../common/clipboardUtils';
import { copyLicenseKeysToMemory } from '../common/licenseUtils';
import { showRowIdByCtrlClick } from '../common/tableActions';
import { userConfigsSelectors } from '../store/userConfigs';
import { unwrapResult } from '@reduxjs/toolkit';
import { showModalMessage } from '../store/modalsSlice';

const thatObjectName = 'permanentLicenses';

class PermanentLicenseContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showPermLicEditDialog: false,
      permanentLicenseEditDialogMode: KeyLicenseEditDialog.Mode.Create,
      showRemoveRestorePermanentLicensesConfirmDialog: false,
      showDeactivatePermLicConfirmDialog: false,
      showPermanentLicenseHistoryDialog: false,
      removeRestorePermanentLicensesConfirmDialogMode: null,
      showReactivatePermLicDialog: false,
      showLicenseActivationDialog: false,
      showExtraInfoDialog: false,
    };
  }

  componentDidMount() {
    setTitleToFilters();
    this.props.fetchPermanentLicenses();
    this.props.fetchProducts();
    this.props.fetchLicLivingStates();
    this.props.fetchViewerProFeatures();
  }

  render() {
    return (
      <OverlayLoader active={this.isLoading()} text={this.getLoaderMessage()}>
        <div>
          <h1>Online permanent licenses</h1>
          {this.renderButtons()}
          {this.renderContent()}
          {this.renderPermLicEditDialog()}
          {this.renderRemoveRestorePermanentLicensesConfirmDialog()}
          {this.renderModalMessage()}
          {this.renderDeactivatePermanentLicensesConfirmDialog()}
          {this.renderReactivatePermanentLicensesDialog()}
          {this.renderPermLicHistoryDialog()}
          {this.renderLicenseActivationDialog()}
        </div>
      </OverlayLoader>
    );
  }

  renderButtons = () => (
    <TooltipButtonGroup className="containerButtonGroup">
      <TooltipElement type="button" text="Refresh" icon="refresh" onClick={this.refreshPermanentLicenses} />
      <TooltipElement type="button" text="Edit license" icon="edit" onClick={this.editPermanentLicense}
        disabled={this.isEditButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Deactivate license" icon="off" onClick={this.deactivatePermLic}
        disabled={this.isDeactivateButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Reactivate license" icon="share-alt" onClick={this.reactivatePermLic}
        disabled={this.isReactivateButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Show license history" icon="list-alt" onClick={this.showLicenseHistory}
        disabled={this.isHistoryButtonDisabled()} />
      <TooltipElement type="button" text="Remove licenses" icon="remove" onClick={this.removePermanentLicenses}
        disabled={this.isRemoveButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Restore licenses" icon="repeat" onClick={this.restorePermanentLicenses}
        disabled={this.isRestoreButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Change license" icon="retweet" onClick={this.changePermanentLicense}
        disabled={this.isChangeButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Download license" icon="download" onClick={this.downloadPermanentLicense}
        disabled={this.isDownloadButtonDisabled()} displayOnlyForWriter />
      {!isClipboardAvailable() ? <React.Fragment /> :
        <TooltipElement type="button" text="Copy to memory" icon="copy" onClick={copyLicenseKeysToMemory(this.props.selectedLicenses)}
          disabled={this.isCopyToMemoryButtonDisabled()} displayOnlyForWriter />
      }
    </TooltipButtonGroup>
  );

  getActionsForFavorites = () => ({
    addToFavorites: this.props.addPermanentLicenseToFavorites,
    removeFromFavorites: this.props.removePermanentLicenseFromFavorites,
  });

  renderContent = () => {
    if (this.props.permanentLicensesFetchingError) {
      return this.renderError(this.props.permanentLicensesFetchingError.toString());
    }
    const tableRowEvents = {
      onClick: this.handleRowClick,
    };

    return <React.Fragment>
      {this.props.productsState.error ? this.renderError(this.props.productsState.error.toString()) : null}
      <TableSelectionManager
        items={this.props.permanentLicenses}
        objectName={thatObjectName}
      >
        <PermanentLicenseTable
          permanentLicenses={this.props.permanentLicenses}
          products={this.props.productsState.data}
          livingStates={this.props.livingStatesState.data}
          actions={this.getActionsForFavorites()}
          requestData={this.props.requestData}
          rowEvents={tableRowEvents}
          features={this.props.featuresState ? this.props.featuresState.data : []}
        />
      </TableSelectionManager>
    </React.Fragment>
  };

  handleRowClick = (e, row) => {
    e.stopPropagation();
    showRowIdByCtrlClick(e, row, 'License ID');
  }

  renderError = message => (
    <p className="text-danger">Error: {message}</p>
  );

  renderPermLicEditDialog = () => (
    <KeyLicenseEditDialog
      temporalType={KeyLicenseTypes.OnlinePermanent}
      mode={this.state.permanentLicenseEditDialogMode}
      onClose={this.closePermLicEditDialog}
      show={this.state.showPermLicEditDialog}
      licKeyType={this.getSelectedLicenseFieldValue('type.code', '')}
      selectedFeatures={
        this.getSelectedLicenseFieldValue('features', [])
          .filter(feature => feature.expirationDate === null)
          .map(feature => feature.id)
      }
      licenseId={this.getSelectedLicenseFieldValue('id')}
      productName={this.getSelectedLicenseFieldValue('productName')}
      clientId={this.getSelectedLicenseFieldValue('client.id')}
      clientName={this.getSelectedLicenseFieldValue('client.fullName')}
      supportDayCount={this.getSelectedLicenseFieldValue('supportDayCount')}
      generationDateTime={this.getSelectedLicenseFieldValue('generationDateTime')}
    />
  );

  renderModalMessage = () => <ModalMessage />;

  refreshPermanentLicenses = () => {
    this.props.fetchPermanentLicenses();
  };

  editPermanentLicense = () => {
    this.setState({ permanentLicenseEditDialogMode: KeyLicenseEditDialog.Mode.Edit });
    this.setState({ showPermLicEditDialog: true });
  };

  changePermanentLicense = () => {
    this.setState({ permanentLicenseEditDialogMode: KeyLicenseEditDialog.Mode.Change });
    this.setState({ showPermLicEditDialog: true });
  };

  closePermLicEditDialog = () => {
    this.setState({ showPermLicEditDialog: false });
  };

  renderRemoveRestorePermanentLicensesConfirmDialog = () => (
    <RemoveRestoreLicsConfirmDialog
      show={this.state.showRemoveRestorePermanentLicensesConfirmDialog}
      onClose={this.closeRemoveRestorePermanentLicensesConfirmDialog}
      licenseIds={this.props.selectedLicensesIds}
      mode={this.state.removeRestorePermanentLicensesConfirmDialogMode}
      licenseType={KeyLicenseTypes.OnlinePermanent}
    />
  );

  removePermanentLicenses = () => {
    this.setState({
      removeRestorePermanentLicensesConfirmDialogMode: RemoveRestoreLicsConfirmDialog.Mode.Remove,
      showRemoveRestorePermanentLicensesConfirmDialog: true
    });
  };

  restorePermanentLicenses = () => {
    this.setState({
      removeRestorePermanentLicensesConfirmDialogMode: RemoveRestoreLicsConfirmDialog.Mode.Restore,
      showRemoveRestorePermanentLicensesConfirmDialog: true
    });
  };

  closeRemoveRestorePermanentLicensesConfirmDialog = () => {
    this.setState({ showRemoveRestorePermanentLicensesConfirmDialog: false });
  };

  renderDeactivatePermanentLicensesConfirmDialog = () => (
    <DeactivateLicConfirmDialog
      temporalType={KeyLicenseTypes.OnlinePermanent}
      show={this.state.showDeactivatePermLicConfirmDialog}
      onClose={this.closeDeactivatePermLicConfirmDialog}
      licenseId={this.getSelectedLicenseFieldValue('id')}
    />
  );

  deactivatePermLic = () => {
    this.setState({ showDeactivatePermLicConfirmDialog: true });
  };

  closeDeactivatePermLicConfirmDialog = () => {
    this.setState({ showDeactivatePermLicConfirmDialog: false });
  };

  renderReactivatePermanentLicensesDialog = () => (
    <ReactivatePermanentLicenseDialog
      show={this.state.showReactivatePermLicDialog}
      onClose={this.closeReactivatePermLicDialog}
      licenseId={this.getSelectedLicenseFieldValue('id')}
      licenseKey={this.getSelectedLicenseFieldValue('key')}
      deviceKey={this.getSelectedLicenseFieldValue('device.key')}
    />
  );

  reactivatePermLic = () => {
    this.setState({ showReactivatePermLicDialog: true });
  };

  closeReactivatePermLicDialog = () => {
    this.setState({ showReactivatePermLicDialog: false });
  };

  renderPermLicHistoryDialog = () => (
    <PermanentLicenseHistoryDialog
      show={this.state.showPermanentLicenseHistoryDialog}
      onClose={this.closePermLicHistoryDialog}
      id={this.getSelectedLicenseFieldValue('id')}
      licenseKey={this.getSelectedLicenseFieldValue('key')}
    />
  );

  showLicenseActivationDialog = () => {
    this.setState({ showLicenseActivationDialog: true });
  };

  closeLicenseActivationDialog = () => {
    this.setState({ showLicenseActivationDialog: false });
  };

  renderLicenseActivationDialog = () => (
    <OnlineLicenseActivatingDialog
      show={this.state.showLicenseActivationDialog}
      onClose={this.closeLicenseActivationDialog}
      licenseId={this.getSelectedLicenseFieldValue('id')}
      onSuccess={() => this.props.downloadLicense({
        licenseId: this.getSelectedLicenseFieldValue('id')
      })}
      licenseType={KeyLicenseTypes.OnlinePermanent}
    />
  );

  downloadPermanentLicense = () => {
    let license = this.props.selectedLicenses[0];
    if (!license)
      return;
    if (license.device === null) {
      this.showLicenseActivationDialog();
      return;
    }
    this.props.downloadLicense({ licenseId: license.id }).then(unwrapResult).catch(() => {
      this.props.showModalMessage("Error", this.props.downloadingError.toString());
    });
  };

  showLicenseHistory = () => {
    this.setState({ showPermanentLicenseHistoryDialog: true });
  };

  closePermLicHistoryDialog = () => {
    this.setState({ showPermanentLicenseHistoryDialog: false });
  };

  showLicenseExtraInfo = () => {
    this.setState({ showExtraInfoDialog: true });
  };

  closeLicenseExtraInfoDialog = () => {
    this.setState({ showExtraInfoDialog: false });
  };

  isLoading = () => this.props.isPermanentLicensesFetching ||
    this.props.isPermanentLicensesCreating ||
    this.props.isPermanentLicenseUpdating ||
    this.props.isPermanentLicensesRemoving ||
    this.props.isPermanentLicensesRestoring ||
    this.props.productsState.isFetching ||
    this.props.livingStatesState.isFetching ||
    this.props.isLicenseAssignmentInProcess;

  getLoaderMessage = () => {
    if (this.props.isPermanentLicensesFetching) {
      return "Permanent licenses loading...";
    }
    if (this.props.isPermanentLicensesCreating) {
      return "License key generating...";
    }
    if (this.props.isPermanentLicenseUpdating) {
      return "Permanent license updating...";
    }
    if (this.props.isPermanentLicensesRemoving) {
      return "Permanent licenses removing...";
    }
    if (this.props.isPermanentLicensesRestoring) {
      return "Permanent licenses restoring...";
    }
    if (this.props.productsState.isFetching) {
      return "Product names loading...";
    }
    if (this.props.livingStatesState.isFetching) {
      return "License living states loading...";
    }
    if (this.props.isLicenseAssignmentInProcess) {
      return "License assignment...";
    }
  };

  isEditButtonDisabled = () => {
    if (this.props.selectedLicenses.length !== 1)
      return true;
    let lic = this.props.selectedLicenses[0];
    return !lic || lic.type === null || lic.type.deprecated || !lic.isAlive;
  };

  isChangeButtonDisabled = () => {
    if (this.props.selectedLicenses.length !== 1)
      return true;
    let lic = this.props.selectedLicenses[0];
    return !lic || !lic.isAlive || lic.type === null;
  };

  isRemoveButtonDisabled = () => {
    let lics = this.props.selectedLicenses;
    return lics.length === 0 || lics.find(lic => !lic.isAlive) !== undefined;
  };

  isRestoreButtonDisabled = () => {
    let lics = this.props.selectedLicenses;
    return lics.length === 0 ||
      lics.find(permanentLicense => permanentLicense.isAlive || permanentLicense.nextId !== null) !== undefined;
  };

  isDeactivateButtonDisabled = () => {
    if (this.props.selectedLicenses.length !== 1)
      return true;
    let lic = this.props.selectedLicenses[0];
    return !lic ||
      !lic.isAlive ||
      lic.device === null;
  };

  isReactivateButtonDisabled = () => {
    if (this.props.selectedLicenses.length !== 1)
      return true;
    let lic = this.props.selectedLicenses[0];
    return !lic ||
      !lic.isAlive;
  };

  isHistoryButtonDisabled = () => this.props.selectedLicenses.length !== 1;

  isDownloadButtonDisabled = () => this.props.selectedLicenses.length !== 1 ||
    this.getSelectedLicenseFieldValue('isAlive') !== true;

  isCopyToMemoryButtonDisabled = () => this.props.selectedLicenses.length === 0;

  getSelectedLicenseFieldValue = (fieldName, defaultValue = null) => {
    const license = this.props.selectedLicenses[0];
    if (!license) return defaultValue;
    try {
      const value = fieldName.split('.').reduce((acc, key) => acc[key], license);
      return value === undefined ? defaultValue : value === null ? defaultValue : value;
    } catch (e) {
      return defaultValue;
    }
  };
}

const mapStateToProps = (state) => ({
  featuresState: objectDataSelectors.featuresState(state),

  isPermanentLicensesFetching: permanentLicensesSelectors.isFetching(state),
  permanentLicenses: permanentLicensesSelectors.selectAll(state),
  permanentLicensesFetchingError: permanentLicensesSelectors.fetchingError(state),
  isPermanentLicensesCreating: permanentLicensesSelectors.isCreating(state),
  isPermanentLicenseUpdating: permanentLicensesSelectors.isUpdating(state),
  isPermanentLicensesRemoving: permanentLicensesSelectors.isRemoving(state),
  isPermanentLicensesRestoring: permanentLicensesSelectors.isRestoring(state),

  productsState: objectDataSelectors.onlineLicenseProductsState(state),

  livingStatesState: objectDataSelectors.onlineLicenseLivingStatesState(state),

  isLicenseAssignmentInProcess: permanentLicensesSelectors.isAssignmentInProcess(state),
  downloadingError: permanentLicensesSelectors.downloadError(state),
  selectedLicenses: permanentLicensesSelectors.selectedLicenses(state),
  selectedLicensesIds: userConfigsSelectors.selectedItems(state, thatObjectName),
});

const actionCreators = {
  fetchViewerProFeatures: () => objectDataActions.fetchObjectData({
    objectName: objectDataNames.features,
    field: 'viewer_pro'
  }),

  fetchPermanentLicenses: permanentLicensesActions.fetchAll,
  fetchProducts: () => objectDataActions.fetchObjectData({ objectName: objectDataNames.onlineLicenseProducts }),
  fetchLicLivingStates: () => objectDataActions.fetchObjectData({ objectName: objectDataNames.onlineLicenseLivingStates }),
  downloadLicense: permanentLicensesActions.download,

  addPermanentLicenseToFavorites: permanentLicensesActions.addToFavorites,
  removePermanentLicenseFromFavorites: permanentLicensesActions.removeFromFavorites,
  showModalMessage: (title, text) => showModalMessage({ title, text }),
};

export default connect(mapStateToProps, actionCreators)(PermanentLicenseContainer);



// WEBPACK FOOTER //
// ./src/containers/permanentLicensesContainer.js